/**
 *  All api related to payment
 */

import _ from 'lodash'
import axios from 'axios'
import { OmnitechApi } from '../../api'
import { cancelRequest } from '../../helpers'
import { isErrorExist, convertResponseToErrorObject } from '../../omnitech-error'

// - create payment request
export function createPaymentRequest({
  paymentRequest = {},
  params = {},
  payload = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'createPaymentRequest',
    () => source.cancel('createPaymentRequest: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: 'payment_requests',
      method: 'post',
      params,
      data: {
        data: {
          paymentRequest
        },
        ...payload
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      paymentRequest: data.paymentRequest
    }))
}

// - create payment response
export function createPaymentResponse({
  paymentResponse = {},
  params = {},
  payload = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'createPaymentResponse',
    () => source.cancel('createPaymentResponse: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: 'payment_responses',
      method: 'post',
      params,
      data: {
        data: {
          paymentResponse
        },
        ...payload
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data, status }) => ({
      data,
      status
    }))
}

export function createApplePayMerchantSession({
  paymentRequestId,
  applePayMerchant,
  params = {},
  payload = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'createApplePayMerchantSession',
    () => source.cancel('createApplePayMerchantSession: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `payment_requests/${paymentRequestId}/apple_pay_merchants/create_session`,
      method: 'post',
      params: {
        ...params
      },
      data: {
        data: {
          applePayMerchant
        },
        ...payload
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      applePayMerchant: _.get(data, 'applePayMerchant', {})
    }))
}

/**
 * fetchQfpayOverseaOauthUrl
 *
 * For QFpay oversea wechat pay only, get oauth url from api after order is created
 */
export function fetchQfpayOverseaOauthUrl({
  orderId,
  paymentProviderId,
  oAuthRedirectURL,
  extraParams = {},
  extraOptions = {}
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'fetchQfpayOverseaOauthUrl',
    () => source.cancel('fetchQfpayOverseaOauthUrl: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: 'payment_requests/qfpay_overseas_oauths/oauth_url',
      method: 'get',
      params: {
        orderId,
        paymentProviderId,
        redirectUrl: oAuthRedirectURL,
        ...extraParams
      },
      cancelToken: source.token,
      ...extraOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      qfpayOverseasOauths: _.get(data, 'qfpayOverseasOauths', {})
    }))
}

// - delete payment provider token
export function deletePaymentProviderToken({
  paymentProviderTokenId,
  params = {},
  payload = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'deletePaymentProviderToken',
    () => source.cancel('deletePaymentProviderToken: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `payment_provider_tokens/${paymentProviderTokenId}`,
      method: 'delete',
      params,
      data: {
        data: {
          paymentProviderToken: {}
        },
        ...payload
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      paymentProviderToken: data.paymentProviderToken
    }))
}

// - fetch payment provider tokens
export function fetchPaymentProviderTokens(options = {}) {
  const { page = 1, ...others } = options
  const params = {
    page,
    ...others
  }
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'fetchPaymentProviderTokens',
    () => source.cancel('fetchPaymentProviderTokens: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `payment_provider_tokens`,
      method: 'get',
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets'
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      paymentProviderTokens: data.paymentProviderTokens,
      pagination: data.paymentProviderTokensPagination,
      next: _.get(data, 'paymentProviderTokensPagination.nextPage') > page
        ? () => fetchPaymentProviderTokens({
          page: page + 1,
          ...others
        })
        : null,
      count: _.get(data, 'paymentProviderTokensPagination.totalCount')
    }))
}
